import React, { useMemo, useState } from "react";
import { CircleMarker, MapContainer, Polygon, TileLayer, Tooltip, useMap } from "react-leaflet";

import { Locality, PollingStation, ServiceRequest, mockProject } from "./sr-map-data";

import "./sr-map.css";

type MapLevel = "constituency" | "locality" | "pollingStation";

const CATEGORIES = ["Water", "Roads", "Electricity", "Sanitation", "Health", "Education", "Other"];

const getRequestColor = (count: number) => {
    if (count >= 50) return "#dc2626";
    if (count >= 30) return "#f59e0b";
    if (count >= 10) return "#eab308";
    return "#16a34a";
};

/* -------------------------------------------------------
 * HELPERS
 * ----------------------------------------------------- */

const getAllPollingStations = (localities: Locality[]): PollingStation[] => {
    return localities.flatMap((locality) => locality.pollingStations);
};

const getAllRequests = (localities: Locality[]): ServiceRequest[] => {
    return getAllPollingStations(localities).flatMap((station) => station.requests);
};

/* -------------------------------------------------------
 * MAP FIT
 * ----------------------------------------------------- */

const MapFitBounds = ({ coordinates }: { coordinates: [number, number][] }) => {
    const map = useMap();

    React.useEffect(() => {
        if (!coordinates?.length) return;

        map.fitBounds(coordinates as any, {
            padding: [30, 30],
        });
    }, [coordinates, map]);

    return null;
};

/* -------------------------------------------------------
 * COUNT MARKER
 * ----------------------------------------------------- */

const CountMarker = ({
    position,
    count,
    label,
    selected = false,
    radius = 20,
}: {
    position: [number, number];
    count: number;
    label: string;
    selected?: boolean;
    radius?: number;
}) => {
    const color = getRequestColor(count);

    return (
        <CircleMarker
            center={position}
            radius={selected ? radius + 3 : radius}
            pathOptions={{
                color: selected ? "#111827" : color,
                fillColor: color,
                fillOpacity: selected ? 1 : 0.88,
                weight: selected ? 4 : 2,
            }}
        >
            <Tooltip permanent direction="center" className="sr-map-count-tooltip">
                <span className="sr-map-count-label">
                    <strong>{count}</strong>
                    <small>{label}</small>
                </span>
            </Tooltip>
        </CircleMarker>
    );
};

/* -------------------------------------------------------
 * MAIN DASHBOARD
 * ----------------------------------------------------- */

export const ServiceMapRequestDashboard = () => {
    const constituency = mockProject.constituency;

    /* ---------------------------------------------------
     * MAP STATE
     * ------------------------------------------------- */

    const [level, setLevel] = useState<MapLevel>("constituency");

    const [selectedLocality, setSelectedLocality] = useState<Locality | null>(null);

    const [selectedPollingStation, setSelectedPollingStation] = useState<PollingStation | null>(null);

    /* ---------------------------------------------------
     * FILTER STATE
     * ------------------------------------------------- */

    const [categoryFilter, setCategoryFilter] = useState("All");

    const [statusFilter, setStatusFilter] = useState("All");

    const [priorityFilter, setPriorityFilter] = useState("All");

    const [search, setSearch] = useState("");

    /* ---------------------------------------------------
     * ALL GEOGRAPHIC DATA
     * ------------------------------------------------- */

    const allPollingStations = useMemo(() => getAllPollingStations(constituency.localities), [constituency]);

    const allRequests = useMemo(() => getAllRequests(constituency.localities), [constituency]);

    /* ---------------------------------------------------
     * SCOPED REQUESTS
     *
     * Geographic selection only.
     * Filters below do NOT affect KPIs.
     * ------------------------------------------------- */

    const scopedRequests = useMemo<ServiceRequest[]>(() => {
        if (selectedPollingStation) {
            return selectedPollingStation.requests;
        }

        if (selectedLocality) {
            return selectedLocality.pollingStations.flatMap((station) => station.requests);
        }

        return allRequests;
    }, [allRequests, selectedLocality, selectedPollingStation]);

    /* ---------------------------------------------------
     * FILTERED REQUESTS
     * ------------------------------------------------- */

    const filteredRequests = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();

        return scopedRequests.filter((request) => {
            const matchesCategory = categoryFilter === "All" || request.category === categoryFilter;

            const matchesStatus = statusFilter === "All" || request.status === statusFilter;

            const matchesPriority = priorityFilter === "All" || request.priority === priorityFilter;

            const matchesSearch =
                !normalizedSearch ||
                request.id.toLowerCase().includes(normalizedSearch) ||
                request.subject.toLowerCase().includes(normalizedSearch) ||
                request.assignedTo.toLowerCase().includes(normalizedSearch);

            return matchesCategory && matchesStatus && matchesPriority && matchesSearch;
        });
    }, [scopedRequests, categoryFilter, statusFilter, priorityFilter, search]);

    /* ---------------------------------------------------
     * CATEGORY REPORT
     * ------------------------------------------------- */

    const categoryReport = useMemo(() => {
        return CATEGORIES.map((category) => {
            const requests = scopedRequests.filter((request) => request.category === category);

            const total = requests.length;

            const created = requests.filter((request) => request.status === "Created").length;

            const progress = requests.filter((request) => request.status === "In Progress").length;

            const resolved = requests.filter((request) => request.status === "Resolved").length;

            const rejected = requests.filter((request) => request.status === "Rejected").length;

            return {
                category,
                total,
                created,
                progress,
                resolved,
                rejected,
                percentage: scopedRequests.length > 0 ? Math.round((total / scopedRequests.length) * 100) : 0,
            };
        });
    }, [scopedRequests]);

    /* ---------------------------------------------------
     * KPI VALUES
     * ------------------------------------------------- */

    const total = scopedRequests.length;

    const createdCount = scopedRequests.filter((request) => request.status === "Created").length;

    const progressCount = scopedRequests.filter((request) => request.status === "In Progress").length;

    const resolvedCount = scopedRequests.filter((request) => request.status === "Resolved").length;

    const rejectedCount = scopedRequests.filter((request) => request.status === "Rejected").length;

    /* ---------------------------------------------------
     * SELECTED LOCATION
     * ------------------------------------------------- */

    const selectedLocation = selectedPollingStation || selectedLocality || null;

    const selectedLocationName = selectedPollingStation?.name || selectedLocality?.name || constituency.name;

    const selectedLocationType = selectedPollingStation
        ? "Polling Station"
        : selectedLocality
          ? "Locality"
          : "Constituency";

    /* ---------------------------------------------------
     * SELECTION HANDLERS
     * ------------------------------------------------- */

    const selectLocality = (locality: Locality) => {
        setSelectedLocality(locality);
        setSelectedPollingStation(null);
    };

    const selectPollingStation = (station: PollingStation) => {
        const parentLocality = constituency.localities.find((locality) =>
            locality.pollingStations.some((item) => item.id === station.id)
        );

        setSelectedLocality(parentLocality || null);

        setSelectedPollingStation(station);
    };

    const clearSelection = () => {
        setSelectedLocality(null);
        setSelectedPollingStation(null);
    };

    /* ---------------------------------------------------
     * FILTER RESET
     * ------------------------------------------------- */

    const resetFilters = () => {
        setCategoryFilter("All");
        setStatusFilter("All");
        setPriorityFilter("All");
        setSearch("");
    };

    /* ---------------------------------------------------
     * LEVEL CHANGE
     * ------------------------------------------------- */

    const changeLevel = (nextLevel: MapLevel) => {
        setLevel(nextLevel);
        clearSelection();
    };

    /* ---------------------------------------------------
     * RENDER
     * ------------------------------------------------- */

    return (
        <div className="sr-dashboard">
            {/* =================================================
                HEADER
            ================================================= */}

            <div className="sr-dashboard-header">
                <div>
                    <div className="sr-dashboard-eyebrow">PROJECT SERVICE REQUESTS</div>

                    <h1>{mockProject.name}</h1>

                    <p>
                        Regional service request report for <strong>{constituency.name} Constituency</strong>
                    </p>
                </div>

                <div className="sr-project-badge">Project #{mockProject.id}</div>
            </div>

            {/* =================================================
                BREADCRUMB
            ================================================= */}

            <div className="sr-breadcrumb">
                <span>Project</span>

                <b>›</b>

                <span>{constituency.name}</span>

                {selectedLocality && (
                    <>
                        <b>›</b>
                        <span>{selectedLocality.name}</span>
                    </>
                )}

                {selectedPollingStation && (
                    <>
                        <b>›</b>
                        <span>{selectedPollingStation.name}</span>
                    </>
                )}
            </div>

            {/* =================================================
                KPI
            ================================================= */}

            <div className="sr-kpi-grid">
                <div className="sr-kpi-card">
                    <span>Total Requests</span>

                    <strong>{total}</strong>

                    <small>{selectedLocationType} scope</small>
                </div>

                <div className="sr-kpi-card">
                    <span>Created</span>

                    <strong>{createdCount}</strong>

                    <small>Newly created requests</small>
                </div>

                <div className="sr-kpi-card">
                    <span>In Progress</span>

                    <strong>{progressCount}</strong>

                    <small>Currently assigned</small>
                </div>

                <div className="sr-kpi-card">
                    <span>Resolved</span>

                    <strong>{resolvedCount}</strong>

                    <small>Successfully resolved</small>
                </div>

                <div className="sr-kpi-card">
                    <span>Rejected</span>

                    <strong>{rejectedCount}</strong>

                    <small>Requests rejected</small>
                </div>
            </div>

            {/* =================================================
                MAP CARD
            ================================================= */}

            <div className="sr-map-card">
                <div className="sr-map-header">
                    <div>
                        <h2>Geographic Request Distribution</h2>

                        <p>Select a geographic level to view request density and boundaries.</p>
                    </div>

                    <div className="sr-map-levels">
                        {(["constituency", "locality", "pollingStation"] as MapLevel[]).map((item) => (
                            <button
                                key={item}
                                className={level === item ? "active" : ""}
                                onClick={() => changeLevel(item)}
                            >
                                {item === "constituency"
                                    ? "Constituency"
                                    : item === "locality"
                                      ? "Locality"
                                      : "Polling Station"}
                            </button>
                        ))}
                    </div>
                </div>

                {/* =================================================
                    MAP
                ================================================= */}

                <div className="sr-map-wrapper">
                    <MapContainer center={[25.5145, 90.2035]} zoom={13} className="sr-map" zoomControl>
                        <TileLayer
                            attribution="&copy; OpenStreetMap contributors"
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />

                        {/* =========================================
                            CONSTITUENCY
                        ========================================= */}

                        <Polygon
                            positions={constituency.coordinates}
                            pathOptions={{
                                color: "#4f46e5",
                                fillColor: "#6366f1",
                                fillOpacity: level === "constituency" ? 0.16 : 0.05,
                                weight: level === "constituency" ? 4 : 3,
                            }}
                        >
                            <Tooltip sticky>
                                <strong>{constituency.name} Constituency</strong>
                                <br />
                                {constituency.requestCount} requests
                            </Tooltip>
                        </Polygon>

                        <MapFitBounds coordinates={constituency.coordinates} />

                        {/* =========================================
                            CONSTITUENCY COUNT
                        ========================================= */}

                        {level === "constituency" && (
                            <CountMarker
                                position={[25.5145, 90.2035]}
                                count={constituency.requestCount}
                                label="Constituency"
                                radius={27}
                            />
                        )}

                        {/* =========================================
                            LOCALITY BOUNDARIES
                        ========================================= */}

                        {(level === "locality" || level === "pollingStation") &&
                            constituency.localities.map((locality) => (
                                <React.Fragment key={locality.id}>
                                    <Polygon
                                        positions={locality.coordinates}
                                        pathOptions={{
                                            color:
                                                selectedLocality?.id === locality.id ? "#2563eb" : "#7c3aed",

                                            fillColor: "#8b5cf6",

                                            fillOpacity: selectedLocality?.id === locality.id ? 0.3 : 0.1,

                                            weight: selectedLocality?.id === locality.id ? 4 : 2,
                                        }}
                                        eventHandlers={{
                                            click: () => selectLocality(locality),
                                        }}
                                    >
                                        <Tooltip sticky>
                                            <strong>{locality.name}</strong>
                                            <br />
                                            {locality.requestCount} requests
                                        </Tooltip>
                                    </Polygon>

                                    <CountMarker
                                        position={locality.coordinates[0]}
                                        count={locality.requestCount}
                                        label={locality.name}
                                        selected={selectedLocality?.id === locality.id}
                                        radius={23}
                                    />
                                </React.Fragment>
                            ))}

                        {/* =========================================
                            POLLING STATIONS
                        ========================================= */}

                        {level === "pollingStation" &&
                            allPollingStations.map((station) => (
                                <React.Fragment key={station.id}>
                                    <Polygon
                                        positions={station.coordinates}
                                        pathOptions={{
                                            color:
                                                selectedPollingStation?.id === station.id
                                                    ? "#1d4ed8"
                                                    : "#111827",

                                            fillColor:
                                                selectedPollingStation?.id === station.id
                                                    ? "#60a5fa"
                                                    : "#c4b5fd",

                                            fillOpacity:
                                                selectedPollingStation?.id === station.id ? 0.5 : 0.2,

                                            weight: selectedPollingStation?.id === station.id ? 4 : 1.2,
                                        }}
                                        eventHandlers={{
                                            click: () => selectPollingStation(station),
                                        }}
                                    >
                                        <Tooltip sticky>
                                            <strong>{station.name}</strong>
                                            <br />
                                            Part No. {station.partNumber}
                                            <br />
                                            {station.requestCount} requests
                                        </Tooltip>
                                    </Polygon>

                                    <CountMarker
                                        position={station.coordinates[0]}
                                        count={station.requestCount}
                                        label={`Part ${station.partNumber}`}
                                        selected={selectedPollingStation?.id === station.id}
                                        radius={16}
                                    />
                                </React.Fragment>
                            ))}
                    </MapContainer>

                    {/* =================================================
                        LEGEND
                    ================================================= */}

                    <div className="sr-map-legend">
                        <strong>Request Density</strong>

                        <div>
                            <i className="legend-dot low" />
                            0–9
                        </div>

                        <div>
                            <i className="legend-dot medium" />
                            10–29
                        </div>

                        <div>
                            <i className="legend-dot high" />
                            30–49
                        </div>

                        <div>
                            <i className="legend-dot critical" />
                            50+
                        </div>
                    </div>
                </div>
            </div>

            {/* =================================================
                LOCATION SUMMARY
            ================================================= */}

            <div className="sr-location-summary">
                <div>
                    <span>REPORTING LOCATION</span>

                    <h2>{selectedLocationName}</h2>
                </div>

                <div className="location-total">
                    <span>Requests</span>

                    <strong>{total}</strong>
                </div>

                {selectedLocation && <button onClick={clearSelection}>Clear Selection</button>}
            </div>

            {/* =================================================
                CATEGORY ANALYSIS
            ================================================= */}

            <div className="sr-analysis-card">
                <div className="sr-section-heading">
                    <div>
                        <h2>Service Request Category Analysis</h2>

                        <p>
                            Request distribution for <strong>{selectedLocationName}</strong>
                        </p>
                    </div>
                </div>

                {/* CATEGORY CARDS */}

                <div className="sr-category-cards">
                    {categoryReport.map((item) => (
                        <div
                            key={item.category}
                            className={`sr-category-card ${
                                categoryFilter === item.category ? "selected" : ""
                            }`}
                            onClick={() =>
                                setCategoryFilter(categoryFilter === item.category ? "All" : item.category)
                            }
                        >
                            <span>{item.category}</span>

                            <strong>{item.total}</strong>

                            <small>{item.percentage}% of requests</small>

                            <small>
                                {item.created} created · {item.progress} in progress · {item.resolved}{" "}
                                resolved · {item.rejected} rejected
                            </small>
                        </div>
                    ))}
                </div>

                {/* CATEGORY TABLE */}

                <div className="sr-category-table-wrapper">
                    <table className="sr-category-table">
                        <thead>
                            <tr>
                                <th>Category</th>
                                <th>Total</th>
                                <th>%</th>
                                <th>Created</th>
                                <th>In Progress</th>
                                <th>Resolved</th>
                                <th>Rejected</th>
                            </tr>
                        </thead>

                        <tbody>
                            {categoryReport.map((item) => (
                                <tr key={item.category}>
                                    <td>
                                        <strong>{item.category}</strong>
                                    </td>

                                    <td>{item.total}</td>

                                    <td>{item.percentage}%</td>

                                    <td>{item.created}</td>

                                    <td>{item.progress}</td>

                                    <td>{item.resolved}</td>

                                    <td>{item.rejected}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* =================================================
                DETAILED REPORT
            ================================================= */}

            <div className="sr-request-card">
                <div className="sr-request-header">
                    <div>
                        <h2>Detailed Service Request Report</h2>

                        <p>
                            Showing <strong>{filteredRequests.length}</strong> of{" "}
                            <strong>{scopedRequests.length}</strong> requests for{" "}
                            <strong>{selectedLocationName}</strong>
                        </p>
                    </div>

                    <button className="sr-reset-button" onClick={resetFilters}>
                        Reset Filters
                    </button>
                </div>

                {/* =================================================
                    FILTERS
                ================================================= */}

                <div className="sr-filter-bar">
                    <input
                        placeholder="Search ID, request or assignee..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />

                    <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                        <option value="All">All Categories</option>

                        {CATEGORIES.map((category) => (
                            <option key={category} value={category}>
                                {category}
                            </option>
                        ))}
                    </select>

                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="All">All Status</option>

                        <option value="Created">Created</option>

                        <option value="In Progress">In Progress</option>

                        <option value="Resolved">Resolved</option>

                        <option value="Rejected">Rejected</option>
                    </select>

                    <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                        <option value="All">All Priority</option>

                        <option value="High">High</option>

                        <option value="Medium">Medium</option>

                        <option value="Low">Low</option>
                    </select>
                </div>

                {/* =================================================
                    REQUEST TABLE
                ================================================= */}

                <div className="sr-table-wrapper">
                    <table className="sr-table">
                        <thead>
                            <tr>
                                <th>SR ID</th>
                                <th>Request</th>
                                <th>Category</th>
                                <th>Priority</th>
                                <th>Status</th>
                                <th>Assigned To</th>
                                <th>Created</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filteredRequests.length > 0 ? (
                                filteredRequests.map((request) => (
                                    <tr key={request.id}>
                                        <td>
                                            <strong>{request.id}</strong>
                                        </td>

                                        <td>{request.subject}</td>

                                        <td>{request.category}</td>

                                        <td>
                                            <span className={`priority ${request.priority.toLowerCase()}`}>
                                                {request.priority}
                                            </span>
                                        </td>

                                        <td>
                                            <span
                                                className={`status ${request.status
                                                    .toLowerCase()
                                                    .replace(" ", "-")}`}
                                            >
                                                {request.status}
                                            </span>
                                        </td>

                                        <td>{request.assignedTo}</td>

                                        <td>{request.createdAt}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="empty-state">
                                        No service requests found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
